import { Backdrop, Card, CardContent, CardHeader, Fade, Grid, Modal, Typography, useTheme } from '@mui/material';
import Papa from 'papaparse';
import React, { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { WO2Checkbox } from 'src/common';
import WO2Button from 'src/common/components/button/Button';
import { useStyles } from 'src/themes';
import { ClientCsvRow, ClientSearchResult } from '../../store/types';

interface Props {
  handleClose: () => void;
  appendClients: (clients: ClientSearchResult[], allowTerminatedClients: boolean) => void;
  isOpen: boolean;
}

export const ReportingGenerationClientCsvModal = (props: Props): JSX.Element => {
  const classes = useStyles();
  const theme = useTheme();
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [rows, setRows] = useState<ClientSearchResult[]>([]);
  const [hasAcknowledgedTerminatedClients, setHasAcknowledgedTerminatedClients] = useState<boolean>(false);

  const filesDisplay = acceptedFiles.map((selectedFile) => (
    <span key={selectedFile.name}>
      {selectedFile.name} - {selectedFile.size} bytes
    </span>
  ));

  const handleValidFile = (files: File[]) => {
    const error = files[0].name.indexOf('.csv') < 0;
    setImportErrors([error ? 'Only CSV documents can be uploaded.' : '']);
    setAcceptedFiles(files);

    Papa.parse(files[0], {
      header: true,
      skipEmptyLines: true,
      complete(results: Papa.ParseResult<ClientCsvRow>) {
        const validClients: ClientSearchResult[] = [];
        const invalidRows: ClientCsvRow[] = [];

        results.data.forEach((row) => {
          if (!row.ID) return;

          const isValidId = typeof row.ID === 'string' && /^\d+$/.test(row.ID);

          if (isValidId) {
            validClients.push({
              clientId: Number(row.ID),
              clientType: row.TYPE,
              clientName: `Client (${row.ID})`,
            });
          } else {
            invalidRows.push(row);
          }
        });

        if (invalidRows.length > 0) {
          const errors = invalidRows.map((row) => {
            const idDisplay = row.ID ?? '[Missing ID]';
            return `Invalid ID - "${idDisplay}"`;
          });
          setImportErrors(errors);
        } else {
          setImportErrors([]);
        }

        setRows(validClients);
      },
    });
  };

  const completeImportCsv = () => {
    props.appendClients(rows, hasAcknowledgedTerminatedClients);
    props.handleClose();
  };

  useEffect(() => {
    setAcceptedFiles([]);
    setImportErrors([]);
    setRows([]);
  }, [props.isOpen]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={props.isOpen}
      onClose={() => {
        props.handleClose();
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={props.isOpen}>
        <Card
          elevation={0}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <CardHeader title={<Typography variant="h3">Import .csv</Typography>} />
          <CardContent>
            <Grid container spacing={2} direction="column">
              <Grid item>
                <Dropzone onDrop={(files) => handleValidFile(files)} multiple={false}>
                  {({ getRootProps, getInputProps }) => (
                    <div
                      data-testid="dropzone"
                      style={{ padding: theme.spacing(1), margin: 0 }}
                      {...getRootProps({ className: classes.dropzone })}
                    >
                      <input
                        {...getInputProps({
                          accept: '.csv',
                          type: 'file',
                        })}
                      />
                      <p>Drag and drop your file here, or click to select file</p>
                    </div>
                  )}
                </Dropzone>
              </Grid>
              {filesDisplay && filesDisplay.length > 0 && (
                <Grid item>
                  <Typography>File: {filesDisplay}</Typography>
                </Grid>
              )}
              {importErrors && importErrors.length > 0 && (
                <Grid item container direction="column" spacing={2}>
                  <Grid item>
                    <Typography color="error">
                      {importErrors.length} row(s) skipped due to missing or non-numeric client IDs.
                    </Typography>
                  </Grid>
                  <Grid item maxHeight={150} overflow="auto">
                    {importErrors.map((error, index) => (
                      <Typography key={index} color="error">
                        {error}
                      </Typography>
                    ))}
                  </Grid>
                </Grid>
              )}
              <Grid item>
                <WO2Checkbox
                    label="Include Terminated Clients - CSV only"
                    onChangeHandler={(value: boolean) => {
                      setHasAcknowledgedTerminatedClients(value);
                    }}
                    checked={hasAcknowledgedTerminatedClients}
                  />
              </Grid>
              <Grid item container spacing={2} mt={2} justifyContent="space-between">
                <Grid item>
                  <WO2Button variant="outlined" onClick={props.handleClose}>
                    Cancel
                  </WO2Button>
                </Grid>
                {acceptedFiles && acceptedFiles.length > 0 && rows.length > 0 && (
                  <Grid item>
                    <WO2Button variant="contained" onClick={completeImportCsv}>
                      Import
                    </WO2Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Fade>
    </Modal>
  );
};
